/**
 * Use-case showcase pages (/use-cases/:slug). Each entry is a real site a
 * customer published with HyVay; the screenshots are static captures stored
 * under public/images/landing/use-cases so the page never depends on the
 * customer's site being up (and never iframes it — they send X-Frame-Options).
 */
/** `w`/`h` are the image's pixel size; galleries size columns by aspect ratio so rows share one height. */
export interface UseCaseShot { src: string; alt: string; caption: string; w: number; h: number }
export interface UseCaseFeature { title: string; desc: string }
export interface UseCaseAdmin {
    title: string;
    emphasis: string;
    lead: string;
    capabilities: UseCaseFeature[];
    /** Screenshots of the admin portal; the section renders text-only while this is empty. */
    shots: UseCaseShot[];
}

export interface UseCase {
    slug: string;
    /** Label in the nav dropdown. */
    label: string;
    eyebrow: string;
    /** Headline; `emphasis` is rendered in italic serif like the landing's h1. */
    title: string;
    emphasis: string;
    lead: string;
    client: { name: string; org: string; url: string; host: string; eventDate: string };
    /** Which HyVay package the project was delivered under. */
    packageLabel: string;
    /** Present when the project is a dynamic app with an admin portal (custom package). */
    admin?: UseCaseAdmin;
    hero: UseCaseShot;
    gallery: UseCaseShot[];
    features: UseCaseFeature[];
    /** Seeds the home composer when the visitor clicks "Build one like this". */
    prompt: string;
    seo: { title: string; description: string };
}

const BASE = '/images/landing/use-cases';

export const USE_CASES: UseCase[] = [
    {
        slug: 'conference',
        label: 'Conference',
        eyebrow: 'Use case · Conference',
        title: 'A conference site that',
        emphasis: 'fills the room',
        lead: 'Indonesia Digital Leadership Summit 2026 is a one-day executive conference hosted by Magister Teknologi Informasi, Fakultas Ilmu Komputer Universitas Indonesia. Its event site was built and published with HyVay on the faculty’s own domain.',
        client: {
            name: 'Indonesia Digital Leadership Summit 2026',
            org: 'Fakultas Ilmu Komputer, Universitas Indonesia',
            url: 'https://idls.cs.ui.ac.id/',
            host: 'idls.cs.ui.ac.id',
            eventDate: '18 October 2026',
        },
        packageLabel: 'Custom application',
        admin: {
            title: 'Behind the site,',
            emphasis: 'an admin portal',
            lead: 'IDLS 2026 was delivered under the custom application package: a dynamic app with a database, not a static page. The committee runs the whole event from a private portal with participants, invitations, check-in and settings, and the public site reads from it.',
            capabilities: [
                { title: 'Speakers and sessions', desc: 'Add speakers, write session descriptions, and assign each session to a room and time slot. The agenda on the public site updates from here.' },
                { title: 'Registrations', desc: 'Attendee sign-ups land in the portal, where the team can review and export them.' },
                { title: 'Invitations', desc: 'Send invitations to selected guests and track who has responded.' },
                { title: 'Seat limits per session', desc: 'Parallel sessions run in separate rooms with different capacities, so each session has its own limit and closes when it is full.' },
                { title: 'Check-in on the day', desc: 'Scan the QR code from each attendee’s ticket email at the door, or look them up by name, email or ticket ID as a fallback.' },
            ],
            // Captures supplied by the customer; no portal address is shown and the signed-in username is blurred.
            shots: [
                { src: `${BASE}/conference-idls-admin-login.webp`, alt: 'IDLS 2026 event portal committee sign-in screen', caption: 'Committee sign-in', w: 916, h: 948 },
                { src: `${BASE}/conference-idls-admin-checkin.webp`, alt: 'Event management portal check-in screen with QR scanner and manual lookup', caption: 'Check-in', w: 1913, h: 961 },
            ],
        },
        hero: { src: `${BASE}/conference-idls-hero.webp`, alt: 'IDLS 2026 home page with countdown and register button', caption: 'Home', w: 1600, h: 900 },
        gallery: [
            { src: `${BASE}/conference-idls-highlights.webp`, alt: 'Summit highlights with event poster and main event rundown', caption: 'Highlights & poster', w: 1600, h: 1111 },
            { src: `${BASE}/conference-idls-agenda.webp`, alt: 'Summit agenda cards with session times and rooms', caption: 'Agenda', w: 1600, h: 833 },
        ],
        features: [
            { title: 'Countdown and registration', desc: 'A live countdown to the event day with a “Register now” button in the hero, the nav, and the footer so no visitor has to hunt for it.' },
            { title: 'Program and agenda', desc: 'Pre-event and main-event rundown, parallel tracks, and session cards with times and rooms. Updated in Studio whenever the schedule changes.' },
            { title: 'Speakers, venue and FAQ', desc: 'A speaker grid, a venue card with a Google Maps link, and an FAQ accordion, all on one scrolling page.' },
            { title: 'Own domain', desc: 'Published to the faculty’s subdomain, so the site carries the university’s address instead of a builder’s.' },
        ],
        prompt: 'Build a one-page conference website — hero with event date, venue and a countdown, a “Register now” button, a program/agenda section with session times, a speaker grid, a venue section with a Google Maps link, and an FAQ.',
        seo: {
            title: 'Conference website use case',
            description: 'How Fakultas Ilmu Komputer UI published the Indonesia Digital Leadership Summit 2026 site with HyVay: countdown, registration, agenda, speakers, venue and FAQ on their own domain.',
        },
    },
    // Hidden until each has a published showcase site:
    // 'Restaurant menu', 'Event invitation', 'Service booking', 'Community & events'.
];

export const findUseCase = (slug: string | undefined): UseCase | undefined => USE_CASES.find((u) => u.slug === slug);
